'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyFormat, useModal, useTurnkey } from '@turnkey/react-wallet-kit';
import { useEffect, useMemo } from 'react';
import { useAuth } from '../../../../auth/state/use-auth.js';
import { useActiveWalletSlot } from '../../../../auth/wallets/active-wallet-context.js';
import { applyDeviceWalletAvailability, persistResolvedActiveWalletSelection, } from '../../../../auth/wallets/device-availability.js';
import { ArchivedWalletsProvider, WalletsProvider, } from '../../../../auth/wallets/wallets-context.js';
// ── Bare fetch helpers (go through the SDK fetch-shim) ────────────────────────
/**
 * SPEC 009 FILE 14: `GET /v1/wallets` + `GET /v1/wallets/archived` were
 * collapsed into `GET /v1/me?kind=wallets` — one route returning
 * `{ data: { kind: 'wallets', active: [...], archived: [...] } }`.
 *
 * The wallet list feeds a PROVIDER mounted on every page — a failed read
 * must NEVER crash the provider tree. Any non-OK status (401/403 signed
 * out, 5xx backend hiccup, proxy ECONNREFUSED) resolves to empty arrays;
 * the query surfaces the failure via its own `error`/`isError` state.
 */
async function fetchMeWallets(signal) {
    const res = await fetch('/v1/me?kind=wallets', {
        credentials: 'include',
        signal: signal ?? null,
    });
    if (!res.ok)
        return { active: [], archived: [] };
    const json = (await res.json());
    const data = json !== null && typeof json === 'object' && 'data' in json
        ? json.data
        : json;
    const arm = (data ?? {});
    return { active: arm.active ?? [], archived: arm.archived ?? [] };
}
async function sdkRegisterWallet(params) {
    const body = {
        publicKey: params.publicKey,
        walletType: params.walletType,
        label: params.label,
    };
    /* v8 ignore start -- defensive: both callers (createWallet, importWallet mutation fns) always pass both fields, so the omit-path (undefined arm) is unreachable. */
    if (params.turnkeyWalletId !== undefined)
        body.turnkeyWalletId = params.turnkeyWalletId;
    if (params.turnkeySubOrgId !== undefined)
        body.turnkeySubOrgId = params.turnkeySubOrgId;
    /* v8 ignore stop */
    const res = await fetch('/v1/wallets/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    if (!res.ok)
        throw new Error(`POST /v1/wallets/register failed: ${res.status}`);
    const json = (await res.json());
    const data = json !== null && typeof json === 'object' && 'data' in json
        ? json.data
        : json;
    return data;
}
async function sdkRenameWallet(walletId, label) {
    const res = await fetch(`/v1/wallets/${encodeURIComponent(walletId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ label }),
    });
    if (!res.ok)
        throw new Error(`PATCH /v1/wallets/${walletId} failed: ${res.status}`);
}
async function sdkUnlinkWallet(walletId) {
    const res = await fetch(`/v1/wallets/${encodeURIComponent(walletId)}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (!res.ok)
        throw new Error(`DELETE /v1/wallets/${walletId} failed: ${res.status}`);
}
async function sdkArchiveWallet(walletId) {
    const res = await fetch(`/v1/wallets/${encodeURIComponent(walletId)}/archive`, {
        method: 'PATCH',
        credentials: 'include',
    });
    if (!res.ok)
        throw new Error(`PATCH /v1/wallets/${walletId}/archive failed: ${res.status}`);
}
async function sdkRecoverWallet(walletId) {
    const res = await fetch(`/v1/wallets/${encodeURIComponent(walletId)}/recover`, {
        method: 'PATCH',
        credentials: 'include',
    });
    if (!res.ok)
        throw new Error(`PATCH /v1/wallets/${walletId}/recover failed: ${res.status}`);
}
// ── Query keys ────────────────────────────────────────────────────────────────
export const WALLETS_QUERY_KEY = ['wallets'];
export const ARCHIVED_WALLETS_QUERY_KEY = ['wallets', 'archived'];
const WALLET_ENVELOPE_QUERY_KEY = ['wallets', 'envelope'];
// ── Wallet shape mapping ──────────────────────────────────────────────────────
function toSdkWallet(entry) {
    return {
        id: entry.id,
        address: entry.publicKey,
        label: entry.label,
        type: entry.walletType === 'embedded'
            ? 'embedded'
            : entry.walletType === 'imported'
                ? 'imported'
                : 'external',
        isAvailable: entry.walletType !== 'external',
        isPrimary: entry.isPrimary,
        archivedAt: entry.archivedAt ?? null,
        totalVolume: entry.totalVolume,
        totalTrades: entry.totalTrades,
        realizedPnl: entry.realizedPnl,
        unrealizedPnl: entry.unrealizedPnl,
        totalPnl: entry.totalPnl,
        marketsTraded: entry.marketsTraded,
        turnkeySubOrgId: entry.turnkeySubOrgId ?? null,
        createdAt: entry.createdAt,
    };
}
// ── Utility ───────────────────────────────────────────────────────────────────
// ALLOW-UNKNOWN: Error handler receives errors from catch blocks which are inherently untyped
function getTurnkeyErrorMessage(err) {
    if (!(err instanceof Error))
        return String(err);
    const parts = [err.message];
    if (err.cause instanceof Error) {
        parts.push(err.cause.message);
    }
    return parts.join(' ');
}
// ── Public query helpers ──────────────────────────────────────────────────────
/**
 * Fetch wallets from backend. Exported so `auth-success-handler` can route its
 * fetch through `queryClient.fetchQuery({ queryKey: WALLETS_QUERY_KEY })`,
 * sharing the in-flight request with `useWallets()`.
 */
export async function fetchWallets(queryClient) {
    return (await fetchWalletEnvelope(queryClient)).active.slice();
}
async function fetchArchivedWallets(queryClient) {
    return (await fetchWalletEnvelope(queryClient)).archived.slice();
}
async function fetchWalletEnvelope(queryClient) {
    if (queryClient === undefined)
        return requestWalletEnvelope();
    return queryClient.fetchQuery({
        queryKey: WALLET_ENVELOPE_QUERY_KEY,
        queryFn: ({ signal }) => requestWalletEnvelope(signal),
        staleTime: 0,
    });
}
async function requestWalletEnvelope(signal) {
    const envelope = await fetchMeWallets(signal);
    return {
        active: envelope.active.map(toSdkWallet),
        archived: envelope.archived.map(toSdkWallet),
    };
}
/** Invalidate the wallet cache from outside the React tree. */
export function invalidateWalletCache(queryClient) {
    return queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
}
/** Returns a function that invalidates the wallet cache. Use in components. */
export function useInvalidateWalletCache() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
}
function useSdkWalletsValue(enabled, onActiveWalletRepairWarning) {
    const queryClient = useQueryClient();
    const { createWallet: turnkeyCreateWallet, refreshWallets, handleImportPrivateKey, handleExportWalletAccount, handleExportPrivateKey, fetchPrivateKeys, disconnectWalletAccount, fetchWalletProviders, session: turnkeySession, } = useTurnkey();
    const activeSubOrgId = turnkeySession?.organizationId;
    const { closeModal: closeTurnkeyModal } = useModal();
    const { isAuthenticated, isLoading: authIsLoading } = useAuth();
    const { activeWalletId, isHydrated, setActiveWalletId } = useActiveWalletSlot();
    const canFetch = enabled && isAuthenticated && !authIsLoading;
    const query = useQuery({
        queryKey: WALLETS_QUERY_KEY,
        queryFn: ({ client }) => fetchWallets(client),
        staleTime: 5 * 60_000,
        gcTime: 30 * 60_000,
        enabled: canFetch,
        retry: false,
        refetchOnWindowFocus: false,
    });
    const hasExternalWallet = query.data?.some((wallet) => wallet.type === 'external') === true;
    const providersQuery = useQuery({
        queryKey: [...WALLETS_QUERY_KEY, 'providers'],
        queryFn: async () => {
            try {
                const providers = await fetchWalletProviders();
                return providers.map((provider) => ({
                    connectedAddresses: [...provider.connectedAddresses],
                }));
            }
            catch (error) {
                console.warn('[wallets] provider availability lookup failed', {
                    errorKind: error instanceof Error ? error.name : typeof error,
                });
                throw error;
            }
        },
        enabled: canFetch && hasExternalWallet,
        retry: false,
        refetchOnWindowFocus: true,
    });
    const walletsWithAvailability = useMemo(() => applyDeviceWalletAvailability(query.data ?? [], (providersQuery.data ?? []).flatMap((provider) => provider.connectedAddresses)), [providersQuery.data, query.data]);
    useEffect(() => {
        if (!enabled ||
            !isAuthenticated ||
            authIsLoading ||
            !isHydrated ||
            query.isLoading ||
            query.error !== null ||
            providersQuery.isLoading) {
            return;
        }
        try {
            persistResolvedActiveWalletSelection(walletsWithAvailability, activeWalletId, setActiveWalletId);
        }
        catch {
            const persistedWallet = walletsWithAvailability.find((wallet) => wallet.id === activeWalletId);
            onActiveWalletRepairWarning?.({
                reason: 'persist_failed',
                persistedWalletType: persistedWallet?.type ?? 'missing',
                walletCount: walletsWithAvailability.length,
                eligibleWalletCount: walletsWithAvailability.filter((wallet) => wallet.archivedAt === null && wallet.isAvailable).length,
            });
        }
    }, [
        activeWalletId,
        authIsLoading,
        enabled,
        isAuthenticated,
        isHydrated,
        onActiveWalletRepairWarning,
        providersQuery.isLoading,
        query.error,
        query.isLoading,
        setActiveWalletId,
        walletsWithAvailability,
    ]);
    // ── Create Wallet ─────────────────────────────────────────────────────────
    const createMutation = useMutation({
        mutationFn: async (params) => {
            const walletId = await turnkeyCreateWallet({
                walletName: params.label,
                accounts: ['ADDRESS_FORMAT_SOLANA'],
            });
            const updatedWallets = await refreshWallets();
            const created = updatedWallets.find((w) => w.walletId === walletId);
            const firstAccount = created?.accounts[0];
            if (!firstAccount) {
                throw new Error('Wallet created but no account address returned');
            }
            const address = firstAccount.address;
            if (activeSubOrgId === undefined || activeSubOrgId.length === 0) {
                throw new Error('Cannot register wallet — no active Turnkey session');
            }
            const registered = await sdkRegisterWallet({
                turnkeyWalletId: walletId,
                turnkeySubOrgId: activeSubOrgId,
                publicKey: address,
                walletType: 'embedded',
                label: params.label,
            });
            return {
                id: registered.id,
                address: registered.publicKey,
                label: params.label,
                type: 'embedded',
                isAvailable: true,
                isPrimary: false,
                archivedAt: null,
                totalVolume: '0',
                totalTrades: 0,
                realizedPnl: '0',
                unrealizedPnl: '0',
                totalPnl: '0',
                marketsTraded: 0,
                turnkeySubOrgId: activeSubOrgId,
                createdAt: new Date().toISOString(),
            };
        },
        onSuccess: (newWallet) => {
            queryClient.setQueryData(WALLETS_QUERY_KEY, (old) => [
                ...(old ?? []),
                newWallet,
            ]);
            setActiveWalletId(newWallet.id);
        },
    });
    // ── Import Wallet ─────────────────────────────────────────────────────────
    const importMutation = useMutation({
        mutationFn: async (params) => {
            let returnedId;
            try {
                returnedId = await handleImportPrivateKey({
                    curve: 'CURVE_ED25519',
                    addressFormats: ['ADDRESS_FORMAT_SOLANA'],
                    keyFormat: KeyFormat.Solana,
                    ...(params.label ? { keyName: params.label } : {}),
                });
            }
            catch (err) {
                const message = getTurnkeyErrorMessage(err);
                if (message.includes('already been imported')) {
                    throw new Error('This wallet has already been imported to your account.');
                }
                throw err;
            }
            const privateKeys = await fetchPrivateKeys();
            const importedKey = privateKeys.find((pk) => pk.privateKeyId === returnedId);
            const solanaAddress = importedKey?.addresses?.find((a) => a.format === 'ADDRESS_FORMAT_SOLANA');
            if (!solanaAddress?.address) {
                throw new Error('Wallet imported but no Solana address found');
            }
            const address = solanaAddress.address;
            const label = params.label ?? 'Imported Wallet';
            if (activeSubOrgId === undefined || activeSubOrgId.length === 0) {
                throw new Error('Cannot register wallet — no active Turnkey session');
            }
            const registered = await sdkRegisterWallet({
                turnkeyWalletId: returnedId,
                turnkeySubOrgId: activeSubOrgId,
                publicKey: address,
                walletType: 'imported',
                label,
            });
            return {
                id: registered.id,
                address: registered.publicKey,
                label,
                type: 'imported',
                isAvailable: true,
                isPrimary: false,
                archivedAt: null,
                totalVolume: '0',
                totalTrades: 0,
                realizedPnl: '0',
                unrealizedPnl: '0',
                totalPnl: '0',
                marketsTraded: 0,
                turnkeySubOrgId: activeSubOrgId,
                createdAt: new Date().toISOString(),
            };
        },
        onSuccess: (newWallet) => {
            queryClient.setQueryData(WALLETS_QUERY_KEY, (old) => [
                ...(old ?? []),
                newWallet,
            ]);
            setActiveWalletId(newWallet.id);
        },
    });
    // ── Export Wallet ─────────────────────────────────────────────────────────
    async function exportWallet(params) {
        try {
            if (params.walletType === 'imported') {
                const privateKeys = await fetchPrivateKeys();
                const matchingKey = privateKeys.find((pk) => pk.addresses?.some((a) => a.format === 'ADDRESS_FORMAT_SOLANA' && a.address === params.address));
                if (!matchingKey?.privateKeyId) {
                    throw new Error('Could not find the imported key in Turnkey.');
                }
                await handleExportPrivateKey({
                    privateKeyId: matchingKey.privateKeyId,
                    keyFormat: KeyFormat.Solana,
                });
            }
            else {
                await handleExportWalletAccount({
                    address: params.address,
                    keyFormat: KeyFormat.Solana,
                });
            }
            return 'exported';
        }
        catch (err) {
            const message = getTurnkeyErrorMessage(err);
            if (message.includes('canceled') || message.includes('cancelled')) {
                // SILENT-CATCH: user cancelled the Turnkey export modal — not an error
                return 'cancelled';
            }
            closeTurnkeyModal();
            throw err;
        }
    }
    // ── Rename Wallet ─────────────────────────────────────────────────────────
    const renameMutation = useMutation({
        mutationFn: async (params) => {
            await sdkRenameWallet(params.walletId, params.label);
            const current = queryClient.getQueryData(WALLETS_QUERY_KEY);
            const existing = current?.find((w) => w.id === params.walletId);
            return {
                id: params.walletId,
                address: existing?.address ?? '',
                label: params.label,
                type: existing?.type ?? 'external',
                isAvailable: existing?.isAvailable ?? false,
                isPrimary: existing?.isPrimary ?? false,
                archivedAt: existing?.archivedAt ?? null,
                totalVolume: existing?.totalVolume ?? '0',
                totalTrades: existing?.totalTrades ?? 0,
                realizedPnl: existing?.realizedPnl ?? '0',
                unrealizedPnl: existing?.unrealizedPnl ?? '0',
                turnkeySubOrgId: existing?.turnkeySubOrgId ?? null,
                totalPnl: existing?.totalPnl ?? '0',
                marketsTraded: existing?.marketsTraded ?? 0,
                createdAt: existing?.createdAt ?? '',
            };
        },
        onSuccess: (updated) => {
            queryClient.setQueryData(WALLETS_QUERY_KEY, (old) => (old ?? []).map((w) => (w.id === updated.id ? updated : w)));
        },
    });
    // ── Unlink Wallet ─────────────────────────────────────────────────────────
    const unlinkMutation = useMutation({
        mutationFn: async (walletId) => {
            const currentWallets = queryClient.getQueryData(WALLETS_QUERY_KEY);
            const wallet = currentWallets?.find((w) => w.id === walletId);
            if (wallet?.type === 'external') {
                try {
                    const providers = await fetchWalletProviders();
                    const matchingProvider = providers.find((p) => p.connectedAddresses.some((addr) => addr.toLowerCase() === wallet.address.toLowerCase()));
                    if (matchingProvider) {
                        await disconnectWalletAccount(matchingProvider);
                    }
                }
                catch {
                    // SILENT-CATCH: Turnkey disconnect is best-effort
                }
            }
            await sdkUnlinkWallet(walletId);
        },
        onSuccess(...args) {
            const walletId = args[1];
            const previous = queryClient.getQueryData(WALLETS_QUERY_KEY);
            const removed = previous?.find((w) => w.id === walletId);
            queryClient.setQueryData(WALLETS_QUERY_KEY, (old) => (old ?? []).filter((w) => w.id !== walletId));
            const remainingWallets = walletsWithAvailability.filter((wallet) => wallet.id !== walletId);
            persistResolvedActiveWalletSelection(remainingWallets, activeWalletId, setActiveWalletId);
            if (removed?.type === 'external') {
                const archivedRow = {
                    ...removed,
                    archivedAt: new Date().toISOString(),
                };
                queryClient.setQueryData(ARCHIVED_WALLETS_QUERY_KEY, (old) => {
                    const filtered = (old ?? []).filter((w) => w.id !== archivedRow.id);
                    return [archivedRow, ...filtered];
                });
            }
        },
    });
    // ── Archive Wallet ────────────────────────────────────────────────────────
    const archiveMutation = useMutation({
        mutationFn: async (walletId) => {
            await sdkArchiveWallet(walletId);
        },
        onSuccess(...args) {
            const walletId = args[1];
            queryClient.setQueryData(WALLETS_QUERY_KEY, (old) => (old ?? []).filter((w) => w.id !== walletId));
            queryClient.invalidateQueries({ queryKey: ARCHIVED_WALLETS_QUERY_KEY });
            const remainingWallets = walletsWithAvailability.filter((wallet) => wallet.id !== walletId);
            persistResolvedActiveWalletSelection(remainingWallets, activeWalletId, setActiveWalletId);
        },
    });
    // ── Recover Wallet ────────────────────────────────────────────────────────
    const recoverMutation = useMutation({
        mutationFn: async (walletId) => {
            await sdkRecoverWallet(walletId);
        },
        onSuccess(...args) {
            const walletId = args[1];
            queryClient.setQueryData(ARCHIVED_WALLETS_QUERY_KEY, (old) => (old ?? []).filter((w) => w.id !== walletId));
            queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
        },
    });
    return {
        value: {
            wallets: walletsWithAvailability,
            isLoading: query.isLoading || providersQuery.isLoading,
            // Provider discovery controls external availability, not embedded wallet readiness.
            error: query.error,
            createWallet: createMutation.mutateAsync,
            importWallet: importMutation.mutateAsync,
            exportWallet,
            renameWallet: renameMutation.mutateAsync,
            unlinkWallet: unlinkMutation.mutateAsync,
            archiveWallet: archiveMutation.mutateAsync,
            recoverWallet: recoverMutation.mutateAsync,
            isCreating: createMutation.isPending,
            isImporting: importMutation.isPending,
            isArchiving: archiveMutation.isPending,
            isRecovering: recoverMutation.isPending,
        },
        canFetch,
    };
}
function useSdkArchivedWalletsValue(enabled) {
    const query = useQuery({
        queryKey: ARCHIVED_WALLETS_QUERY_KEY,
        queryFn: ({ client }) => fetchArchivedWallets(client),
        staleTime: 5 * 60_000,
        gcTime: 30 * 60_000,
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
    });
    return {
        archivedWallets: query.data ?? [],
        isLoading: query.isLoading,
        error: query.error,
    };
}
/**
 * Self-contained wallets provider for web.
 *
 * Mounts inside the `<TurnkeyProvider>` + `<QueryClientProvider>` trees.
 * Drives `WalletsProvider` + `ArchivedWalletsProvider` from a single TanStack
 * Query + Turnkey hook — no frontend bridge required.
 *
 * Replace the old `<WalletsBridge>` with this provider in `layout.tsx`.
 */
export function SdkWalletsProvider({ enabled = true, onActiveWalletRepairWarning, children, }) {
    const { value: walletsValue, canFetch } = useSdkWalletsValue(enabled, onActiveWalletRepairWarning);
    const archivedValue = useSdkArchivedWalletsValue(canFetch);
    return (_jsx(WalletsProvider, { value: walletsValue, children: _jsx(ArchivedWalletsProvider, { value: archivedValue, children: children }) }));
}
//# sourceMappingURL=use-sdk-wallets.js.map